import NextImage, { StaticImageData } from 'next/image';
import { CTAButton } from '../../../components/Clickables/Buttons/CTAButton/CTAButton';
import { AppOnlyPill } from '../../../components/Clickables/Pill/AppOnlyPill';
import { Grid } from '../../../components/Grid/Grid';
import { Heading } from '../../../components/Heading/Heading';
import { Source } from '../../../components/NextImage/Source';
import { Text } from '../../../components/Text/Text';
import { ProPlanId } from '../../../constants/premium';
import { useCurrentURL } from '../../../helpers/hooks/useCurrentURL/useCurrentURL';
import { useIsMobileDevice } from '../../../helpers/hooks/useIsMobileDevice';
import {
  URLParameters,
  companiesURLParameters,
  flowsURLParameters,
} from '../../Onboarding/constants/url';

export interface ProSectionProps {
  title: string;
  description: string;
  sectionImageMobile: StaticImageData;
  sectionImageTablet: StaticImageData;
  sectionImageDesktop: StaticImageData;
  sectionImageAlt: string;
  phoneRight?: boolean;
  appPreviewImage: StaticImageData;
  appPreviewImageAlt: string;
  proPlanId?: ProPlanId;
  partnerFlow?: boolean;
  appOnlyPill: boolean;
}

export const ProSection = ({
  title,
  description,
  sectionImageMobile,
  sectionImageTablet,
  sectionImageDesktop,
  sectionImageAlt,
  phoneRight = false,
  appPreviewImage,
  appPreviewImageAlt,
  proPlanId,
  partnerFlow = false,
  appOnlyPill,
}: ProSectionProps) => {
  const isMobileDevice = useIsMobileDevice();
  const { addParameters } = useCurrentURL();
  const redeemLink = addParameters({
    [URLParameters.Company]: companiesURLParameters.aspira,
    [URLParameters.Flow]: flowsURLParameters.aspira.becomePro,
  });

  return (
    <div className="mx-auto grid max-w-screen-lg grid-cols-1 grid-rows-1 overflow-hidden bg-lake-fog sm:pb-20">
      <div className="col-start-1 row-start-1">
        <div className="min-h-[300px] w-full object-cover">
          <picture>
            <Source media="(min-width: 990px)" src={sectionImageDesktop} />
            <Source media="(min-width: 600px)" src={sectionImageTablet} />
            <NextImage
              className="aspect-square w-full object-cover sm:aspect-auto"
              src={sectionImageMobile}
              placeholder="blur"
              alt={sectionImageAlt}
              sizes="(min-width: 1280px) 1280px, 100vw"
            />
          </picture>
        </div>

        <Grid className="mx-6 sm:mx-9 md:mx-5">
          <div
            className={`col-start-1 col-end-5 self-end pb-20 pt-[36%] sm:py-0 ${
              // the hard coded padding top pushes text down on a small screen so it isn't hidden behind the phone preview
              phoneRight
                ? 'sm:order-first sm:col-end-7 md:col-start-2'
                : 'sm:col-start-7 sm:col-end-13 md:col-end-12'
            }`}
          >
            {appOnlyPill && <AppOnlyPill className="mt-8 sm:mt-16" />}
            <Heading as="h3" variant="md" className="mb-2.5 mt-2 sm:mt-6 md:mt-8">
              {title}
            </Heading>
            <Text variant="md">{description}</Text>
            {partnerFlow
              ? !isMobileDevice && (
                  <CTAButton
                    type="pro"
                    routerPushTo={redeemLink}
                    source="ProBanner"
                    variant="buttonPro"
                    className="mb-6 w-full px-6 sm:w-max md:px-12"
                  >
                    Sign up to redeem
                  </CTAButton>
                )
              : !isMobileDevice && (
                  <CTAButton
                    type="pro"
                    proPlanId={proPlanId}
                    source="ProSection"
                    variant="buttonPro"
                    className="my-4"
                  >
                    Unlock with Fishbrain Pro
                  </CTAButton>
                )}
          </div>
        </Grid>
      </div>

      <div className="pointer-events-none col-start-1 row-start-1">
        <div className="mx-9 grid h-full grid-cols-1 sm:grid-cols-12 md:mx-5">
          <div
            className={`w-full justify-self-center pt-4 sm:self-end sm:pt-8 md:pt-20 ${
              phoneRight
                ? 'sm:order-last sm:col-start-7 sm:col-end-13 md:col-end-12'
                : 'sm:col-start-1 sm:col-end-7 md:col-start-2'
            }`}
          >
            <NextImage
              className="w-full object-cover"
              src={appPreviewImage}
              alt={appPreviewImageAlt}
              sizes="(min-width: 1680px) 640px,
                  (min-width: 990px) 41vw,
                  (min-width: 600px) 33vw,
                  100vw"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
